import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { bindContextKeyToSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts/selectors';
import { defineMessages, injectIntl } from 'react-intl';
import { withStyles } from '@lifeomic/chroma-react/styles';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import _ from 'lodash';
import { contextKeys } from '../../../../../redux/modules/asyncRequestContexts';
import { surveysActions } from '../../../../../redux/modules/surveys';
import { asyncRequestContextsActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts';
import NotificationHelper from '@lifeomic/phc-web-toolkit/src/redux/util/NotificationHelper';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { InfiniteScroll } from '@lifeomic/chroma-react/components/InfiniteScroll';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import moment from 'moment';
import {
  Survey,
  FullPageContainer,
  questionnaireUtils,
} from '@lifeomic/phc-web-toolkit/src/components/Surveys';
import { patientPropsSelector } from '../../../../../redux/selectors/patientSelector';
import { maskedModeSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/maskedMode';
import DeleteResponseButton from './DeleteResponseButton';
import { projectPickerSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker';
import { userSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/user';
import { v4 as uuid } from 'uuid';

const messages = defineMessages({
  updateSurveyResponseFailure: {
    id: 'patientViewer.internalSurveys.updateSurveyResponse.failure',
    defaultMessage: 'Failed to update survey response',
  },
  fetchResponseSurveyFailure: {
    id: 'patientViewer.internalSurveys.fetchResponseSurveyFailure.failure',
    defaultMessage: 'Failed to load survey associated with this response',
  },
  fetchResponsesFailure: {
    id: 'patientViewer.internalSurveys.fetchResponsesFailure.failure',
    defaultMessage: "Failed to load subject's survey responses",
  },
  notStarted: {
    id: 'patientViewer.internalSurveys.status.notStarted',
    defaultMessage: 'Not Started',
  },
  inProgress: {
    id: 'patientViewer.internalSurveys.status.inProgress',
    defaultMessage: 'In Progress',
  },
  completed: {
    id: 'patientViewer.internalSurveys.status.completed',
    defaultMessage: 'Completed',
  },
  stopped: {
    id: 'patientViewer.internalSurveys.status.stopped',
    defaultMessage: 'Expired',
  },
  survey: {
    id: 'patientViewer.internalSurveys.columns.survey',
    defaultMessage: 'Survey',
  },
  date: {
    id: 'patientViewer.internalSurveys.columns.date',
    defaultMessage: 'Date',
  },
  completedBy: {
    id: 'patientViewer.internalSurveys.columns.completedBy',
    defaultMessage: 'Completed By',
  },
  status: {
    id: 'patientViewer.internalSurveys.columns.status',
    defaultMessage: 'Status',
  },
  loadMore: {
    id: 'patientViewer.internalSurveys.columns.loadMore',
    defaultMessage: 'Load More Responses',
  },
});

const surveyResponsesContextKey =
  contextKeys.patientViewer.fetchSubjectResponses;
const surveyContextKey = contextKeys.patientViewer.fetchResponseSurvey;

const formatStatus = (formatMessage, surveyResponse) => {
  if (questionnaireUtils.isResponseNotStarted(surveyResponse)) {
    return formatMessage(messages.notStarted);
  } else {
    const status = _.get(surveyResponse, 'status');
    switch (status) {
      case 'completed':
        return formatMessage(messages.completed);
      case 'in-progress':
        return formatMessage(messages.inProgress);
      case 'stopped':
        return formatMessage(messages.stopped);
      default:
        return status;
    }
  }
};

const {
  isFetchingSelector: isFetchingSurveyResponsesSelector,
  responsesSelector: surveyResponsesSelector,
  hasNextTokenSelector: surveyResponsesHasNextTokenSelector,
  nextTokenSelector: surveyResponsesNextTokenSelector,
} = bindContextKeyToSelectors(surveyResponsesContextKey);

const {
  isFetchingSelector: isFetchingSurveySelector,
  responsesSelector: surveySelector,
} = bindContextKeyToSelectors(surveyContextKey);

const styles = theme => ({
  loadingContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  surveyContainer: {
    padding: theme.spacing(2),
  },
  loadMoreContainer: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
  },
});

class ResponseList extends PureComponent {
  state = {
    focusedSurveyResponse: null,
    deletedResponseIds: [],
    isOpen: false,
  };

  componentDidMount() {
    this.initializeData();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.dateTickFilter !== prevProps.dateTickFilter ||
      this.props.renderTrigger !== prevProps.renderTrigger
    ) {
      this.initializeData();
    }
  }

  async initializeData(props = this.props) {
    const {
      resetContext,
      fetchResponses,
      intl: { formatMessage },
      patient,
      dateTickFilter: { min, max },
      ignoreTimeFilter,
    } = props;

    resetContext(surveyResponsesContextKey);
    const notification = new NotificationHelper(
      null,
      formatMessage(messages.fetchResponsesFailure),
    );
    const id = _.get(patient, 'id');

    if (!id) {
      console.error('Cannot fetch survey responses without a patient id');
      return;
    }

    const params = {
      patientId: patient.id,
      _sort: '-authored',
      pageSize: 100,
    };

    if (!ignoreTimeFilter) {
      const authored = [];
      if (!_.isNil(min)) {
        const dateVal = new Date(min).toISOString();
        authored.push(`gt${dateVal}`);
      }

      if (!_.isNil(max)) {
        const dateVal = new Date(max).toISOString();
        authored.push(`lt${dateVal}`);
      }
      params.authored = authored;
    }

    await fetchResponses({ params }, surveyResponsesContextKey, notification);
  }

  handleLoadMore = () => {
    const {
      fetchResponses,
      intl: { formatMessage },
      surveyResponsesNextToken,
    } = this.props;

    const notification = new NotificationHelper(
      null,
      formatMessage(messages.fetchResponsesFailure),
    );

    fetchResponses(
      { next: surveyResponsesNextToken },
      surveyResponsesContextKey,
      notification,
    );
  };

  handleResponseDelete = responseId => {
    this.setState({
      deletedResponseIds: [...this.state.deletedResponseIds, responseId],
    });
  };

  getColumns = formatMessage => [
    {
      header: { label: formatMessage(messages.survey) },
      cell: {
        content: response => _.get(response, 'survey'),
      },
    },
    {
      header: { label: formatMessage(messages.status) },
      cell: {
        content: response => _.get(response, 'status'),
      },
    },
    {
      header: { label: formatMessage(messages.date) },
      cell: {
        content: response => _.get(response, 'date'),
      },
    },
    {
      header: { label: formatMessage(messages.completedBy) },
      cell: {
        content: response => _.get(response, 'completedBy'),
      },
    },
  ];

  handleSurveyClose = () => {
    this.setState({ isOpen: false });
  };

  getTableData = () => {
    const {
      surveyResponses,
      intl: { formatMessage },
      isMasked,
      mrnConfig,
      patient,
      statusFilter,
    } = this.props;

    const displayName = _.get(
      patientPropsSelector(patient, isMasked, mrnConfig),
      'name.display',
    );
    return surveyResponses
      .filter(
        surveyResponse =>
          !questionnaireUtils.isConsentQuestionnaireResponse(surveyResponse) &&
          (!statusFilter ||
            questionnaireUtils.hasStatus(surveyResponse, statusFilter)),
      )
      .map(surveyResponse => {
        const rawDate = _.get(surveyResponse, 'authored');
        const momentDate = moment(rawDate);
        const sortValue = momentDate.isValid() ? momentDate.valueOf() : 0;
        const date = momentDate.isValid()
          ? momentDate.format('MM/DD/YY hh:mm A')
          : '';

        const status = formatStatus(formatMessage, surveyResponse);
        const authorDisplayName = _.get(surveyResponse, 'author.display', '');
        const reference = _.get(surveyResponse, 'author.reference', '');
        const completedBy =
          authorDisplayName ||
          (reference === patient.id ? displayName : reference);

        return {
          id: _.get(surveyResponse, 'id'),
          sortValue,
          date,
          survey: _.get(surveyResponse, 'questionnaire.display'),
          status,
          completedBy,
          surveyId: questionnaireUtils.surveyIdFromResponse(surveyResponse),
          surveyResponse,
        };
      })
      .filter(
        surveyResponse =>
          !this.state.deletedResponseIds.includes(surveyResponse.id),
      )
      .sort((resp1, resp2) => resp2.sortValue - resp1.sortValue);
  };

  handleRowClick = ({ surveyId, surveyResponse }) => {
    const {
      resetContext,
      intl: { formatMessage },
      fetchSurvey,
    } = this.props;

    if (!surveyId) {
      console.error('No survey id found for response');
      return;
    }

    resetContext(surveyContextKey);
    const notification = new NotificationHelper(
      null,
      formatMessage(messages.fetchResponseSurveyFailure),
    );

    fetchSurvey(surveyId, surveyContextKey, notification);

    // if editing a completed survey response then
    // the response submitted should be using a different id
    // look at useAllowsResponseResubmission for how editing a completed
    // survey response works
    // TODO: Uncomment the 3 lines below once we fix the issue with editing completed survey responses (see https://lifeomic.atlassian.net/browse/ROR-296?focusedCommentId=290740)
    // Modifying the id of the survey response will cause fetching the response attachments to fail since the id is used to fetch the attachments
    // if (surveyResponse.status === 'completed') {
    //   surveyResponse.id = uuid();
    // }

    this.setState({ focusedSurveyResponse: surveyResponse, isOpen: true });
  };

  handleSurveyResponseUpdate = updatedResponse => {
    const { focusedSurveyResponse } = this.state;
    return new Promise(resolve => {
      this.setState({ focusedSurveyResponse: updatedResponse }, () =>
        resolve(),
      );
    });
  };

  handleSurveyComplete = (isAutoSave, onSaveComplete) => {
    const {
      updateSurveyResponse,
      intl: { formatMessage },
    } = this.props;

    const { focusedSurveyResponse } = this.state;

    if (isAutoSave) {
      return updateSurveyResponse(
        focusedSurveyResponse,
        surveyResponsesContextKey,
      )
        .then(onSaveComplete)
        .finally(() => {
          this.initializeData();
        });
    }

    const notification = new NotificationHelper(
      null,
      formatMessage(messages.updateSurveyResponseFailure),
    );

    updateSurveyResponse(
      focusedSurveyResponse,
      surveyResponsesContextKey,
      notification,
    ).finally(() => {
      this.initializeData();
    });
    this.handleSurveyClose();
  };

  render() {
    const {
      patient,
      isFetchingResponses,
      isFetchingSurvey,
      survey,
      classes,
      surveyResponsesHasNextToken,
      intl: { formatMessage },
      user,
    } = this.props;

    const tableData = this.getTableData();

    const { focusedSurveyResponse, isOpen } = this.state;

    return (
      <>
        {!patient ? (
          <div className={classes.loadingContainer}>
            <DotLoader />
          </div>
        ) : (
          <InfiniteScroll
            hasNextPage={surveyResponsesHasNextToken}
            loading={isFetchingResponses}
            loadMore={this.handleLoadMore}
            scrollOffset={100}
            scrollContainer="parent"
          >
            <TableModule
              config={this.getColumns(formatMessage)}
              data={tableData}
              isLoading={isFetchingResponses}
              onRowClick={this.handleRowClick}
              wrapCellContent
              rowActions={response => (
                <DeleteResponseButton
                  responseId={_.get(response, 'id')}
                  surveyName={_.get(response, 'survey')}
                  surveyDate={_.get(response, 'date')}
                  onResponseDelete={this.handleResponseDelete}
                />
              )}
            />
          </InfiniteScroll>
        )}
        <FullPageContainer
          isOpen={isOpen}
          onClose={this.handleSurveyClose}
          title={_.get(survey, 'title')}
        >
          {!survey || isFetchingSurvey || !user ? (
            <div className={classes.loadingContainer}>
              <DotLoader />
            </div>
          ) : (
            <Survey
              isReadOnly={false}
              questionnaire={survey}
              questionnaireResponse={focusedSurveyResponse}
              onResponseUpdate={this.handleSurveyResponseUpdate}
              onComplete={this.handleSurveyComplete}
              nonSubjectAuthor={{
                reference: user.id,
                display: user.profile.name,
              }}
              autoSave={focusedSurveyResponse !== 'completed'}
              templatePreviewEnabled
              // useAllowsResponseResubmission={true} TODO: Uncomment this line once we fix the issue with editing completed survey responses (see https://lifeomic.atlassian.net/browse/ROR-296?focusedCommentId=290740)
            />
          )}
        </FullPageContainer>
      </>
    );
  }
}

ResponseList.propTypes = {
  patient: PropTypes.object.isRequired,
  dateTickFilter: PropTypes.object.isRequired,
  statusFilter: PropTypes.string,
};

const mapDispatchToProps = {
  resetContext: asyncRequestContextsActions.resetContext,
  fetchSurvey: surveysActions.fetchSurvey,
  fetchResponses: surveysActions.fetchResponses,
  updateSurveyResponse: surveysActions.updateSurveyResponse,
};

const mapStateToProps = state => ({
  surveyResponses: surveyResponsesSelector(state),
  isFetchingResponses: isFetchingSurveyResponsesSelector(state),
  survey: surveySelector(state)[0],
  isFetchingSurvey: isFetchingSurveySelector(state),
  isMasked: maskedModeSelectors.selectShouldMask(state),
  mrnConfig: projectPickerSelectors.selectActiveMrnConfig(state),
  surveyResponsesNextToken: surveyResponsesNextTokenSelector(state),
  surveyResponsesHasNextToken: surveyResponsesHasNextTokenSelector(state),
  user: userSelectors.selectActiveUser(state),
});

export default compose(
  injectIntl,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ResponseList);
